import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { adminListHostEvents, adminPatchHostEvent } from "@/lib/api";
import { Field, PageHeader, StatCell, fmtDate } from "../_shared";

const HOST_STATUSES = [
    { key: "all", label: "All" },
    { key: "received", label: "Received" },
    { key: "under_review", label: "Under Review" },
    { key: "approved", label: "Approved" },
    { key: "declined", label: "Declined" },
];

function HostPill({ status }) {
    const map = {
        received: "bg-white/10 text-ink",
        under_review: "bg-[#07E6F6]/20 text-[#07E6F6] border-[#07E6F6]/40",
        approved: "bg-gradient-cyan text-[#0a0a0a]",
        declined: "bg-white/[0.04] text-ink-soft",
    };
    return (
        <span
            data-testid={`host-status-pill-${status}`}
            className={`inline-flex items-center px-3 py-1.5 rounded-full text-[0.7rem] font-display font-bold uppercase tracking-[0.15em] border border-transparent ${map[status] || map.received}`}
        >
            {(status || "received").replace(/_/g, " ")}
        </span>
    );
}

export default function HostEventsTab() {
    const [data, setData] = useState({ items: [], total: 0, counts_by_status: {} });
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [status, setStatus] = useState("all");
    const [selected, setSelected] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            setData(await adminListHostEvents({ q, status, page: 1, page_size: 100 }));
        } catch (e) {
            toast(e?.response?.data?.detail || "Failed to load host events.");
        } finally {
            setLoading(false);
        }
    }, [q, status]);

    useEffect(() => {
        const t = setTimeout(load, 200);
        return () => clearTimeout(t);
    }, [load]);

    return (
        <div data-testid="host-events-tab">
            <PageHeader
                eyebrow="Inbox · Open"
                title="Host"
                accent="Events"
                description="Open-tier applications from anyone running a community event, anywhere."
            />

            <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-line border border-line rounded-2xl overflow-hidden mb-8">
                <StatCell label="Total" value={data.total} accent />
                {HOST_STATUSES.slice(1).map((s) => (
                    <StatCell key={s.key} label={s.label} value={data.counts_by_status[s.key] || 0} />
                ))}
            </div>

            <div className="grid lg:grid-cols-12 gap-3 mb-6">
                <div className="lg:col-span-5">
                    <input
                        data-testid="host-search"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search name, email, ref, event, city…"
                        className="w-full bg-white/[0.04] border border-line focus:border-[#5fe3c9] rounded-full px-5 py-3 outline-none"
                    />
                </div>
                <div className="lg:col-span-7 flex items-center justify-end gap-2 flex-wrap">
                    {HOST_STATUSES.map((s) => (
                        <button
                            key={s.key}
                            type="button"
                            data-testid={`host-status-${s.key}`}
                            onClick={() => setStatus(s.key)}
                            className={`px-3 py-2 rounded-full font-display font-bold uppercase tracking-[0.15em] text-[0.6rem] transition-colors border ${status === s.key ? "bg-[#f4f4f0] text-[#0a0a0a] border-[#f4f4f0]" : "text-ink-soft border-line hover:text-ink hover:border-ink"}`}
                        >
                            {s.label}
                        </button>
                    ))}
                </div>
            </div>

            <p className="vc-section-label text-ink-soft mb-3">
                {loading ? "Loading…" : `${data.total} host events`}
            </p>

            <div data-testid="host-events-table" className="border border-line rounded-2xl overflow-hidden">
                <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 bg-white/[0.04] border-b border-line vc-section-label text-ink-soft">
                    <div className="col-span-3">Applicant</div>
                    <div className="col-span-3">Event</div>
                    <div className="col-span-2">Location</div>
                    <div className="col-span-2">Date</div>
                    <div className="col-span-2">Status</div>
                </div>
                {data.items.length === 0 && !loading && (
                    <div className="px-5 py-10 text-ink-soft">No host events yet.</div>
                )}
                {data.items.map((a) => (
                    <button
                        key={a.reference_id}
                        type="button"
                        onClick={() => setSelected(a.reference_id)}
                        data-testid={`host-row-${a.reference_id}`}
                        className="w-full text-left grid grid-cols-1 md:grid-cols-12 gap-1 md:gap-4 px-5 py-4 border-b border-line last:border-0 hover:bg-white/[0.03] transition-colors items-center"
                    >
                        <div className="md:col-span-3">
                            <div className="font-display font-bold">{a.full_name}</div>
                            <div className="text-ink-soft text-xs truncate">{a.email}</div>
                            <div className="text-ink-soft font-mono text-[10px] mt-0.5">{a.reference_id}</div>
                        </div>
                        <div className="md:col-span-3 text-sm text-ink truncate">
                            <span className="text-ink-soft text-[10px] uppercase tracking-[0.2em] block">
                                {a.event_type}
                            </span>
                            {a.event_name}
                        </div>
                        <div className="md:col-span-2 text-sm text-ink truncate">{a.event_location}</div>
                        <div className="md:col-span-2 text-sm text-ink-soft">
                            {fmtDate(a.target_date)} · {a.expected_attendees} ppl
                        </div>
                        <div className="md:col-span-2">
                            <HostPill status={a.status} />
                        </div>
                    </button>
                ))}
            </div>

            {selected && (
                <HostDrawer
                    refId={selected}
                    items={data.items}
                    onClose={() => setSelected(null)}
                    onSaved={async () => {
                        setSelected(null);
                        await load();
                    }}
                />
            )}
        </div>
    );
}

function HostDrawer({ refId, items, onClose, onSaved }) {
    const app = items.find((x) => x.reference_id === refId);
    const [status, setStatus] = useState(app?.status || "received");
    const [notes, setNotes] = useState(app?.admin_notes || "");
    const [busy, setBusy] = useState(false);

    if (!app) return null;

    const save = async () => {
        setBusy(true);
        try {
            await adminPatchHostEvent(refId, { status, admin_notes: notes });
            toast("Host event updated.");
            onSaved();
        } catch (e) {
            toast(e?.response?.data?.detail || "Save failed.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <div onClick={onClose} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60]" />
            <aside
                data-testid="host-drawer"
                className="fixed top-0 right-0 h-full w-full sm:w-[560px] bg-[#0a0a0a] border-l border-line z-[70] overflow-y-auto shadow-[-12px_0_40px_rgba(0,0,0,0.5)]"
            >
                <div className="sticky top-0 z-10 bg-[#0a0a0a]/95 backdrop-blur-md border-b border-line px-6 py-4 flex items-center justify-between">
                    <div>
                        <div className="vc-section-label text-[#5fe3c9]">Host event</div>
                        <div className="font-mono text-lg font-bold">{refId}</div>
                    </div>
                    <button onClick={onClose} className="text-ink-soft hover:text-ink text-2xl leading-none">×</button>
                </div>
                <div className="p-6 space-y-7">
                    <div>
                        <div className="vc-section-label text-ink-soft mb-2">Status</div>
                        <div className="flex flex-wrap gap-2">
                            {HOST_STATUSES.slice(1).map((s) => (
                                <button
                                    key={s.key}
                                    type="button"
                                    onClick={() => setStatus(s.key)}
                                    data-testid={`host-drawer-status-${s.key}`}
                                    className={`px-3 py-2 rounded-full font-display font-bold uppercase tracking-[0.15em] text-[0.65rem] border transition-colors ${status === s.key ? "bg-[#f4f4f0] text-[#0a0a0a] border-[#f4f4f0]" : "text-ink-soft border-line hover:border-ink hover:text-ink"}`}
                                >
                                    {s.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <Field label="Admin notes">
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={4}
                            data-testid="host-drawer-notes"
                            className="w-full bg-white/[0.03] border border-line focus:border-[#5fe3c9] rounded-2xl p-4 text-ink outline-none"
                        />
                    </Field>
                    <button
                        type="button"
                        onClick={save}
                        disabled={busy}
                        data-testid="host-drawer-save"
                        className="vc-btn vc-btn-accent w-full justify-center disabled:opacity-60"
                    >
                        {busy ? "Saving…" : "Save changes"}
                    </button>

                    <div className="vc-rule" />

                    <Detail label="Name" value={app.full_name} />
                    <Detail label="Email" value={app.email} mono />
                    <Detail label="Social" value={app.social_url} link />
                    <Detail label="Connection" value={app.emergent_connection} />
                    <Detail label="Organised before" value={app.organised_before ? `Yes — ${app.organised_details}` : "No"} />
                    <div className="vc-rule" />
                    <Detail label="Event name" value={app.event_name} />
                    <Detail label="Event type" value={app.event_type} />
                    <DetailLong label="The idea" value={app.event_idea} />
                    <Detail label="Location" value={app.event_location} />
                    <Detail label="Venue status" value={app.venue_status} />
                    <Detail label="Expected attendees" value={app.expected_attendees} />
                    <Detail label="Target date" value={fmtDate(app.target_date)} />
                    <Detail label="Submitted" value={fmtDate(app.created_at)} />
                </div>
            </aside>
        </>
    );
}

function Detail({ label, value, link, mono }) {
    return (
        <div>
            <div className="vc-section-label text-ink-soft mb-1">{label}</div>
            {link ? (
                <a href={value} target="_blank" rel="noreferrer" className={`text-ink hover:text-gradient break-all ${mono ? "font-mono" : ""}`}>{value}</a>
            ) : (
                <div className={`text-ink break-all ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
            )}
        </div>
    );
}

function DetailLong({ label, value }) {
    return (
        <div>
            <div className="vc-section-label text-ink-soft mb-2">{label}</div>
            <p className="text-ink leading-relaxed whitespace-pre-wrap text-[0.95rem] bg-white/[0.03] border border-line rounded-2xl p-4">
                {value || "—"}
            </p>
        </div>
    );
}
